"use client"

import { createContext, PropsWithChildren, useContext, useEffect } from "react"

import { User } from "@bullseye/types"

import { LoadingFullScreen } from "../../components/LoadingFullScreen"
import { useAccessToken, useMe } from "./hooks"
import type { AuthContext, UserContext } from "./types"

const UserContext = createContext<UserContext | null>(null)

export const useUserContext = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider")
  }
  return context
}

export const useOptionalUserContext = () => {
  return useContext(UserContext)
}

type Props = PropsWithChildren<{
  onError: (error: Error) => void
  authCtx: Omit<AuthContext, "getCredentials">
}>

export const UserProvider = ({ children, onError, authCtx }: Props) => {
  const {
    accessToken,
    isLoading: loadingToken,
    error: tokenError,
  } = useAccessToken()
  const { user, isLoading, error, refetch, isRefetching, key } =
    useMe(accessToken)

  async function refetchUser() {
    const { data, error } = await refetch<User[]>()
    if (error) {
      throw error
    }
    return data?.data?.data?.[0]
  }

  const hasError = !!error
  const hasTokenError = !!tokenError
  const hasMissingUser = !isLoading && !loadingToken && !user && !error && !tokenError

  // Trigger error redirect via effect to avoid returning null during render
  useEffect(() => {
    if (hasError) {
      onError(error as Error)
    } else if (hasTokenError) {
      onError(tokenError as Error)
    } else if (hasMissingUser) {
      onError(new Error("Request to /me returned no user and no error"))
    }
  }, [hasError, hasTokenError, hasMissingUser, error, tokenError])

  // Show loading while initializing or while redirecting after error
  const isInitialLoading = (isLoading && !user) || (loadingToken && !accessToken)
  if (isInitialLoading || hasError || hasTokenError || hasMissingUser) {
    return <LoadingFullScreen />
  }

  return (
    <UserContext.Provider
      value={{
        ...authCtx,
        user,
        refetch: refetchUser,
        isRefetching,
        accessToken,
        queryKey: key,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
